import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Pagination,
  CircularProgress, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, FormControl, InputLabel, Select, MenuItem,
  Tooltip, Alert, Avatar, InputAdornment, Skeleton,
} from "@mui/material";
import EmojiEventsRoundedIcon  from "@mui/icons-material/EmojiEventsRounded";
import EditRoundedIcon          from "@mui/icons-material/EditRounded";
import HistoryRoundedIcon       from "@mui/icons-material/HistoryRounded";
import PlayArrowRoundedIcon     from "@mui/icons-material/PlayArrowRounded";
import SearchRoundedIcon        from "@mui/icons-material/SearchRounded";
import ArrowForwardRoundedIcon  from "@mui/icons-material/ArrowForwardRounded";
import PersonRoundedIcon        from "@mui/icons-material/PersonRounded";
import BookmarkRoundedIcon      from "@mui/icons-material/BookmarkRounded";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  amber:   "#D97706", amberLt: "#FEF3C7",
  emerald: "#059669", emeraldLt:"#D1FAE5",
  rose:    "#E11D48", roseLt:  "#FFE4E6",
  bg:      "#F7FAFC", border:  "#E2E8F0",
  text:    "#0F172A", muted:   "#64748B",
};

const TIERS = ["bronze", "silver", "gold", "platinum"];

const TIER_META = {
  bronze:   { color:"#92400E", bg:"#FEF3C7", border:"#D97706", label:"Bronze",   emoji:"🥉" },
  silver:   { color:"#374151", bg:"#F3F4F6", border:"#9CA3AF", label:"Silver",   emoji:"🥈" },
  gold:     { color:"#B45309", bg:"#FFFBEB", border:"#F59E0B", label:"Gold",     emoji:"🥇" },
  platinum: { color:T.tealDk,  bg:T.tealLt,  border:T.teal,    label:"Platinum", emoji:"💎" },
};

const FILTER_TIERS = [
  { value:"",        label:"All",      color:T.muted,    bg:T.border    },
  { value:"bronze",  label:"Bronze",   color:"#92400E",  bg:"#FEF3C7"   },
  { value:"silver",  label:"Silver",   color:"#374151",  bg:"#F3F4F6"   },
  { value:"gold",    label:"Gold",     color:"#B45309",  bg:"#FFFBEB"   },
  { value:"platinum",label:"Platinum", color:T.tealDk,   bg:T.tealLt    },
];

const TierChip = ({ tier }) => {
  const m = TIER_META[tier] || TIER_META.bronze;
  return (
    <Chip size="small" label={`${m.emoji} ${m.label}`}
      sx={{ bgcolor:m.bg,color:m.color,fontWeight:700,fontSize:11,height:24,border:`1px solid ${m.border}44` }} />
  );
};

const initials = name => {
  const p = (name||"").trim().split(" ");
  return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0]||"?").toUpperCase();
};

const AVATAR_COLORS = [
  [T.tealLt, T.teal],[T.amberLt, T.amber],["#EEF2FF","#4F46E5"],
  [T.emeraldLt, T.emerald],["#FFF0E0","#FF7900"],
];
const avatarBg = name => AVATAR_COLORS[name?.charCodeAt(0) % AVATAR_COLORS.length] || AVATAR_COLORS[0];

// ── Main component ────────────────────────────────────────────────────────
export default function LoyaltyTierAdmin() {
  const [rows,         setRows]         = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [tierFilter,   setTierFilter]   = useState("");
  const [search,       setSearch]       = useState("");

  const [overrideOpen,   setOverrideOpen]   = useState(false);
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideTier,   setOverrideTier]   = useState("bronze");
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideBusy,   setOverrideBusy]   = useState(false);

  const [historyOpen,   setHistoryOpen]   = useState(false);
  const [historyRows,   setHistoryRows]   = useState([]);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyLoading,setHistoryLoading]= useState(false);

  const [evalRunning,  setEvalRunning]  = useState(false);
  const [evalSummary,  setEvalSummary]  = useState(null);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit:25 };
      if (tierFilter) params.tier = tierFilter;
      if (search)     params.search = search;
      const res = await getData(API_ENDPOINTS.dimLoyaltyCustomers, params);
      const r = res?.result;
      if (r) { setRows(r.rows||[]); setTotalPages(r.totalPages||1); }
    } catch(e) { console.error("Loyalty list failed:", e); }
    setLoading(false);
  }, [page, tierFilter, search]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const openOverride = (c) => {
    setOverrideTarget(c); setOverrideTier(c.loyalty_tier||"bronze");
    setOverrideReason(""); setOverrideOpen(true);
  };

  const submitOverride = async () => {
    if (!overrideTarget) return;
    setOverrideBusy(true);
    try {
      await postData(API_ENDPOINTS.dimLoyaltyOverride, {
        customer_id: overrideTarget.customer_id,
        to_tier: overrideTier,
        reason: overrideReason,
      });
      setOverrideOpen(false); await fetchCustomers();
    } catch(e) { alert(e.response?.data?.message || "Override failed"); }
    finally { setOverrideBusy(false); }
  };

  const openHistory = async (c) => {
    setHistoryTarget(c); setHistoryOpen(true);
    setHistoryLoading(true); setHistoryRows([]);
    try {
      const res = await getData(`/api/dim/admin/loyalty/history/${c.customer_id}`);
      setHistoryRows(res?.result?.rows || []);
    } catch(e) { console.error("History fetch:", e); }
    finally { setHistoryLoading(false); }
  };

  const runEvalNow = async () => {
    setEvalRunning(true); setEvalSummary(null);
    try {
      const res = await postData(API_ENDPOINTS.dimLoyaltyEvalNow, {});
      setEvalSummary(res?.result); await fetchCustomers();
    } catch(e) { alert(e.response?.data?.message || "Eval failed"); }
    finally { setEvalRunning(false); }
  };

  const fmt = t => t ? t.charAt(0).toUpperCase()+t.slice(1) : "—";

  return (
    <Box sx={{ bgcolor:T.bg, minHeight:"100vh", pb:4 }}>

      {/* ── Hero ── */}
      <Box sx={{ background:`linear-gradient(135deg,${T.teal} 0%,${T.tealDk} 55%,#005559 100%)`,
        borderRadius:"20px", p:3, mb:3,
        display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:2,
        boxShadow:"0 4px 20px rgba(0,124,132,0.2)" }}>
        <Box sx={{ display:"flex",alignItems:"center",gap:2 }}>
          <Box sx={{ width:46,height:46,borderRadius:"12px",bgcolor:"rgba(255,255,255,0.18)",border:"1px solid rgba(255,255,255,0.25)",display:"flex",alignItems:"center",justifyContent:"center" }}>
            <EmojiEventsRoundedIcon sx={{ fontSize:24,color:"#fff" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize:22,fontWeight:800,color:"#fff",fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              Loyalty Tiers
            </Typography>
            <Typography sx={{ fontSize:13,color:"rgba(255,255,255,0.75)",mt:0.3 }}>
              DIM-001 · Customer tier management &amp; history
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" disableElevation
          startIcon={evalRunning ? <CircularProgress size={14} sx={{ color:"inherit" }} /> : <PlayArrowRoundedIcon sx={{ fontSize:16 }} />}
          onClick={runEvalNow} disabled={evalRunning}
          sx={{ borderRadius:"12px",textTransform:"none",fontWeight:700,
            bgcolor:"rgba(255,255,255,0.2)",border:"1px solid rgba(255,255,255,0.4)",
            "&:hover":{ bgcolor:"rgba(255,255,255,0.3)" },
            "&.Mui-disabled":{ bgcolor:"rgba(255,255,255,0.08)",color:"rgba(255,255,255,0.35)" } }}>
          {evalRunning ? "Running…" : "Run Tier Evaluation"}
        </Button>
      </Box>

      {/* ── Eval result ── */}
      {evalSummary && (
        <Alert severity="success" variant="filled" onClose={()=>setEvalSummary(null)}
          sx={{ borderRadius:"12px",mb:2 }}>
          Evaluation complete — <strong>{evalSummary.evaluated}</strong> customers checked,{" "}
          <strong>{evalSummary.promoted}</strong> promoted.
        </Alert>
      )}

      {/* ── Filters ── */}
      <Box sx={{ display:"flex",gap:1.5,mb:2.5,flexWrap:"wrap",alignItems:"center" }}>
        {/* Tier pills */}
        <Box sx={{ display:"flex",gap:1,flexWrap:"wrap" }}>
          {FILTER_TIERS.map(f => {
            const active = tierFilter === f.value;
            return (
              <Box key={f.value} onClick={()=>{ setTierFilter(f.value); setPage(1); }}
                sx={{ px:1.5,py:0.6,borderRadius:"10px",cursor:"pointer",fontWeight:700,fontSize:13,userSelect:"none",
                  bgcolor: active ? f.color : "#fff",
                  color:   active ? "#fff"  : f.color,
                  border:`1.5px solid ${active ? f.color : f.color+"66"}`,
                  transition:"all 0.15s", "&:hover":{ bgcolor: active ? f.color : f.bg },
                }}>
                {f.label}
              </Box>
            );
          })}
        </Box>
        {/* Search */}
        <TextField size="small" placeholder="Search by name or mobile…" value={search}
          onChange={e=>setSearch(e.target.value)}
          onKeyDown={e=>{ if(e.key==="Enter"){ setPage(1); fetchCustomers(); } }}
          InputProps={{
            startAdornment:<InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize:17,color:T.muted }} /></InputAdornment>,
            sx:{ borderRadius:"12px",fontSize:13 }
          }}
          sx={{ minWidth:260, "& .MuiOutlinedInput-root":{ borderRadius:"12px" } }} />
      </Box>

      {/* ── Table card ── */}
      <Card sx={{ borderRadius:"16px",border:`1px solid ${T.border}`,boxShadow:"0 1px 4px rgba(15,23,42,0.06)",overflow:"hidden" }}>
        <Box sx={{ height:3,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />

        {loading ? (
          <Box sx={{ p:2.5 }}>
            {Array.from({length:6}).map((_,i)=>(
              <Skeleton key={i} variant="rounded" height={52} sx={{ mb:1,borderRadius:"10px" }} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ py:8,textAlign:"center" }}>
            <Box sx={{ width:60,height:60,borderRadius:"16px",bgcolor:T.tealLt,display:"inline-flex",alignItems:"center",justifyContent:"center",mb:2 }}>
              <EmojiEventsRoundedIcon sx={{ fontSize:30,color:T.teal }} />
            </Box>
            <Typography sx={{ fontSize:15,fontWeight:700,color:T.text,fontFamily:"'Plus Jakarta Sans',sans-serif" }}>
              No customers found
            </Typography>
            <Typography sx={{ fontSize:13,color:T.muted,mt:0.5 }}>
              Try a different tier filter or clear the search.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ "& .MuiTableCell-root":{
                  bgcolor:"#F8FAFC",fontWeight:700,fontSize:11,
                  textTransform:"uppercase",letterSpacing:"0.4px",color:T.muted,
                  py:1.25,borderBottom:`2px solid ${T.border}`,
                }}}>
                  <TableCell>Customer</TableCell>
                  <TableCell>Mobile</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell>Bookings</TableCell>
                  <TableCell>Total Spend</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(c => {
                  const fullName = `${c.first_name||""} ${c.last_name||""}`.trim() || "Customer";
                  const [abg, afg] = avatarBg(fullName);
                  return (
                    <TableRow key={c.customer_id} hover sx={{ "&:hover":{ bgcolor:"#F8FAFC" },"& .MuiTableCell-root":{ py:1.25,borderBottom:`1px solid ${T.border}` } }}>

                      <TableCell>
                        <Box sx={{ display:"flex",alignItems:"center",gap:1.5 }}>
                          <Avatar sx={{ width:32,height:32,bgcolor:abg,color:afg,fontSize:12,fontWeight:800 }}>
                            {initials(fullName)}
                          </Avatar>
                          <Box>
                            <Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>{fullName}</Typography>
                            <Typography sx={{ fontSize:11,color:T.muted }}>ID #{c.customer_id}</Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize:13,color:T.text,fontFamily:"monospace" }}>
                          {c.mobile_num ?? "—"}
                        </Typography>
                      </TableCell>

                      <TableCell><TierChip tier={c.loyalty_tier} /></TableCell>

                      <TableCell>
                        <Box sx={{ display:"flex",alignItems:"center",gap:0.5 }}>
                          <BookmarkRoundedIcon sx={{ fontSize:13,color:T.muted }} />
                          <Typography sx={{ fontSize:13,fontWeight:600,color:T.text }}>
                            {c.bookings_count ?? 0}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>
                          ₹{Number(c.total_spend??0).toLocaleString()}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{ display:"flex",justifyContent:"flex-end",gap:0.5 }}>
                          <Tooltip title="Override tier">
                            <IconButton size="small" onClick={()=>openOverride(c)}
                              sx={{ color:T.amber,bgcolor:T.amberLt,borderRadius:"8px","&:hover":{ bgcolor:"#FDE68A" } }}>
                              <EditRoundedIcon sx={{ fontSize:15 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Tier history">
                            <IconButton size="small" onClick={()=>openHistory(c)}
                              sx={{ color:T.teal,bgcolor:T.tealLt,borderRadius:"8px","&:hover":{ bgcolor:"#b2e8ea" } }}>
                              <HistoryRoundedIcon sx={{ fontSize:15 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {totalPages > 1 && (
        <Box sx={{ display:"flex",justifyContent:"center",mt:3 }}>
          <Pagination count={totalPages} page={page} onChange={(_,p)=>setPage(p)}
            sx={{ "& .MuiPaginationItem-root":{ borderRadius:"10px" },
                  "& .Mui-selected":{ bgcolor:`${T.teal} !important`,color:"#fff" } }} />
        </Box>
      )}

      {/* ── Override dialog ── */}
      <Dialog open={overrideOpen} onClose={()=>setOverrideOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx:{ borderRadius:"20px",overflow:"hidden" } }}>
        <Box sx={{ height:4,background:`linear-gradient(90deg,${T.amber},${T.amberLt})` }} />
        <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif",display:"flex",alignItems:"center",gap:1.5 }}>
          <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:T.amberLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
            <EditRoundedIcon sx={{ fontSize:17,color:T.amber }} />
          </Box>
          Override Loyalty Tier
        </DialogTitle>
        <DialogContent sx={{ pt:1 }}>
          {overrideTarget && (
            <Box sx={{ display:"flex",flexDirection:"column",gap:2,mt:0.5 }}>
              {/* Customer card */}
              <Box sx={{ p:1.5,bgcolor:T.bg,borderRadius:"12px",border:`1px solid ${T.border}`,display:"flex",alignItems:"center",gap:1.5 }}>
                <PersonRoundedIcon sx={{ fontSize:18,color:T.muted }} />
                <Box>
                  <Typography sx={{ fontSize:13,fontWeight:700,color:T.text }}>
                    {overrideTarget.first_name} {overrideTarget.last_name}
                  </Typography>
                  <Box sx={{ display:"flex",alignItems:"center",gap:1,mt:0.3 }}>
                    <Typography sx={{ fontSize:11,color:T.muted }}>ID #{overrideTarget.customer_id}</Typography>
                    <Typography sx={{ fontSize:11,color:T.muted }}>·</Typography>
                    <Typography sx={{ fontSize:11,color:T.muted }}>Current:</Typography>
                    <TierChip tier={overrideTarget.loyalty_tier} />
                  </Box>
                </Box>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>New Tier</InputLabel>
                <Select value={overrideTier} label="New Tier" onChange={e=>setOverrideTier(e.target.value)}
                  sx={{ borderRadius:"12px" }}>
                  {TIERS.map(t => {
                    const m = TIER_META[t];
                    return (
                      <MenuItem key={t} value={t}>
                        <Box sx={{ display:"flex",alignItems:"center",gap:1 }}>
                          <Typography>{m.emoji}</Typography>
                          <Typography sx={{ fontWeight:600 }}>{m.label}</Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField fullWidth size="small" label="Reason (audit trail)" multiline rows={2}
                value={overrideReason} onChange={e=>setOverrideReason(e.target.value)}
                InputProps={{ sx:{ borderRadius:"12px" } }} />

              <Box sx={{ p:1.5,bgcolor:T.tealLt,borderRadius:"12px",border:`1px solid ${T.teal}33` }}>
                <Typography sx={{ fontSize:12,color:T.tealDk,lineHeight:1.5 }}>
                  Auto-promotion can only move tiers <strong>up</strong>. Use this override to demote,
                  jump multiple tiers, or correct an error. All changes are logged to the loyalty history.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3,py:2.5,gap:1,borderTop:`1px solid ${T.border}` }}>
          <Button onClick={()=>setOverrideOpen(false)} variant="outlined"
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,borderColor:T.border,color:T.muted,flex:1 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation onClick={submitOverride} disabled={overrideBusy}
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:700,bgcolor:T.teal,"&:hover":{ bgcolor:T.tealDk },flex:1 }}>
            {overrideBusy ? "Saving…" : "Apply Override"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── History dialog ── */}
      <Dialog open={historyOpen} onClose={()=>setHistoryOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx:{ borderRadius:"20px",overflow:"hidden" } }}>
        <Box sx={{ height:4,background:`linear-gradient(90deg,${T.teal},${T.tealLt})` }} />
        <DialogTitle sx={{ fontWeight:800,fontFamily:"'Plus Jakarta Sans',sans-serif",display:"flex",alignItems:"center",gap:1.5 }}>
          <Box sx={{ width:34,height:34,borderRadius:"9px",bgcolor:T.tealLt,display:"flex",alignItems:"center",justifyContent:"center" }}>
            <HistoryRoundedIcon sx={{ fontSize:17,color:T.teal }} />
          </Box>
          {historyTarget
            ? `${historyTarget.first_name} ${historyTarget.last_name} — Tier History`
            : "Tier History"}
        </DialogTitle>
        <DialogContent sx={{ pt:1 }}>
          {historyLoading ? (
            <Box sx={{ py:3,display:"flex",justifyContent:"center" }}>
              <CircularProgress size={28} sx={{ color:T.teal }} />
            </Box>
          ) : historyRows.length === 0 ? (
            <Box sx={{ py:4,textAlign:"center" }}>
              <HistoryRoundedIcon sx={{ fontSize:36,color:T.border,mb:1 }} />
              <Typography sx={{ fontSize:13,color:T.muted }}>No tier changes recorded yet.</Typography>
            </Box>
          ) : (
            <Box sx={{ display:"flex",flexDirection:"column",gap:1,mt:0.5 }}>
              {historyRows.map(h => (
                <Box key={h.loyalty_history_id} sx={{ p:1.5,borderRadius:"12px",border:`1px solid ${T.border}`,bgcolor:"#fff" }}>
                  <Box sx={{ display:"flex",alignItems:"center",justifyContent:"space-between",mb:0.5 }}>
                    <Box sx={{ display:"flex",alignItems:"center",gap:1 }}>
                      <TierChip tier={h.from_tier} />
                      <ArrowForwardRoundedIcon sx={{ fontSize:14,color:T.muted }} />
                      <TierChip tier={h.to_tier} />
                    </Box>
                    <Typography sx={{ fontSize:11,color:T.muted }}>
                      {new Date(h.createdAt).toLocaleString("en-IN")}
                    </Typography>
                  </Box>
                  <Box sx={{ display:"flex",alignItems:"center",gap:1,mt:0.5 }}>
                    <Typography sx={{ fontSize:12,color:T.muted,flex:1,fontStyle:h.reason?"normal":"italic" }}>
                      {h.reason||"No reason provided"}
                    </Typography>
                    <Typography sx={{ fontSize:11,color:T.muted }}>
                      by {h.triggered_by_admin_id ? `admin #${h.triggered_by_admin_id}` : "system"}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px:3,py:2,borderTop:`1px solid ${T.border}` }}>
          <Button onClick={()=>setHistoryOpen(false)} variant="outlined" fullWidth
            sx={{ borderRadius:"10px",textTransform:"none",fontWeight:600,borderColor:T.border,color:T.muted }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
